import { useRef, useCallback, useState } from 'react'

export interface VideoPlayerControls {
  /** Unmute the video */
  unmute: () => void
  /** Mute the video */
  mute: () => void
  /** Toggle mute state */
  toggleMute: () => void
  /** Play the video */
  play: () => void
  /** Pause the video */
  pause: () => void
  /** Toggle play/pause state */
  togglePlay: () => void
  /** Set volume (0-100) */
  setVolume: (volume: number) => void
}

export interface VideoPlayerRef extends VideoPlayerControls {
  /** Get the current muted state */
  isMuted: () => boolean
  /** Get the current playing state */
  isPlaying: () => boolean
}

export interface UseVideoPlayerControlsReturn {
  /** Ref to pass to the VideoPlayer component */
  playerRef: React.RefObject<VideoPlayerRef>
  /** Control methods for the video player */
  controls: VideoPlayerControls
  /** Current muted state (reactive) */
  isMuted: boolean
  /** Current playing state (reactive) */
  isPlaying: boolean
}

/**
 * Hook to control a VideoPlayer component
 *
 * @example
 * ```tsx
 * const { playerRef, controls, isMuted } = useVideoPlayerControls()
 *
 * return (
 *   <>
 *     <VideoPlayer ref={playerRef} ... />
 *     <Button onPress={controls.unmute}>Unmute</Button>
 *   </>
 * )
 * ```
 */
export function useVideoPlayerControls(): UseVideoPlayerControlsReturn {
  const playerRef = useRef<VideoPlayerRef>(null)
  const [isMuted, setIsMuted] = useState(true)
  const [isPlaying, setIsPlaying] = useState(false)

  const unmute = useCallback(() => {
    playerRef.current?.unmute()
    setIsMuted(false)
  }, [])

  const mute = useCallback(() => {
    playerRef.current?.mute()
    setIsMuted(true)
  }, [])

  const toggleMute = useCallback(() => {
    playerRef.current?.toggleMute()
    setIsMuted((prev) => !prev)
  }, [])

  const play = useCallback(() => {
    playerRef.current?.play()
    setIsPlaying(true)
  }, [])

  const pause = useCallback(() => {
    playerRef.current?.pause()
    setIsPlaying(false)
  }, [])

  const togglePlay = useCallback(() => {
    playerRef.current?.togglePlay()
    setIsPlaying((prev) => !prev)
  }, [])

  const setVolume = useCallback((volume: number) => {
    playerRef.current?.setVolume(volume)
    setIsMuted(volume === 0)
  }, [])

  return {
    playerRef,
    controls: {
      unmute,
      mute,
      toggleMute,
      play,
      pause,
      togglePlay,
      setVolume,
    },
    isMuted,
    isPlaying,
  }
}
